import React, { forwardRef, memo, useCallback, useEffect, useImperativeHandle, useRef, useState } from "react";
import { FilePond, registerPlugin } from "react-filepond";
import FilePondPluginFileValidateType from "filepond-plugin-file-validate-type";
import { Dialog } from "primereact/dialog";
import { Button } from "primereact/button";
import { BsEye, BsDownload } from "react-icons/bs";

import { baseURL, zlink } from "service/HttpService";
import InformationBox from "components/common/Message/InformationBox/InformationBox";
import ConfirmationBox from "components/common/Message/ConfirmationBox/ConfirmationBox";

registerPlugin(FilePondPluginFileValidateType);

export interface ImageUploadZoneHandle {
    getTempStorageDirectory: () => string;
    getFileCount: () => number;
    isUploading: () => boolean;
    reset: () => void;
    // Empties the zone AND deletes any already-saved images from the server. reset() only clears the
    // client side, so images dropped that way came back on the next Edit.
    deleteAllImages: () => Promise<void>;
}

export interface InitialAttachment {
    attachmentId: string;
    fileName: string;
}

export interface UploadedImage {
    id: string;
    fileName: string;
    tempStorageDirectory: string;
    previewUrl: string;
    file: any;
}

interface ImageUploadZonePropType {
    fieldKey: string;
    labelIdle: string;
    maxFiles?: number;
    disabled?: boolean;
    initialAttachments?: InitialAttachment[];
    // Equipment the initialAttachments were fetched for. Previous/Next changes equipmentId before
    // the new lists arrive, so seeding must wait until this matches.
    attachmentsEquipmentId?: any;
    // Changes when the attachment lists were reloaded after a save, allowing the reseed below to
    // replace the user's now-saved picks with their seeded equivalents.
    attachmentsRefreshKey?: any;
    uniqueInspectionTaskId?: any;
    equipmentId?: any;
    onFilesChange?: (tempStorageDirectory: string, fileCount: number, unsavedFileCount?: number) => void;
    onUploadingChange?: (isUploading: boolean) => void;
    // Busy for any reason - uploading a new pick or downloading already-saved images. Used to
    // disable the form's buttons; kept separate from onUploadingChange, which gates submit only.
    onBusyChange?: (isBusy: boolean) => void;
}

const ACCEPTED_FILE_TYPES = ["image/png", "image/jpeg"];
const ACCEPTED_FILE_EXTENSIONS = [".png", ".jpg", ".jpeg"];
const FILE_ERROR_STATUSES = [6, 8, 10];

const isRejectedFile = (item: any) =>
    FILE_ERROR_STATUSES.includes(item?.status) || !!item?.error;
export const MAX_FILE_SIZE_MB = 10;
const MAX_FILE_SIZE_BYTES = MAX_FILE_SIZE_MB * 1024 * 1024;

const formatFileSize = (bytes: number) => {
    const megabytes = bytes / (1024 * 1024);
    return megabytes >= 1 ? `${megabytes.toFixed(1)}MB` : `${Math.max(1, Math.round(bytes / 1024))}KB`;
};

const ImageUploadZone = forwardRef<ImageUploadZoneHandle, ImageUploadZonePropType>((props, ref) => {

    const {
        fieldKey,
        labelIdle,
        maxFiles = 4,
        disabled = false,
        initialAttachments,
        attachmentsEquipmentId,
        attachmentsRefreshKey,
        uniqueInspectionTaskId,
        equipmentId,
        onFilesChange = () => { },
        onUploadingChange = () => { },
        onBusyChange = () => { },
    } = props;

    const fileRef = useRef<any>(null);
    const [files, setFiles] = useState<any>([]);
    const [isUploading, setIsUploading] = useState(false);
    // True while already-saved images are being downloaded into the zone.
    const [isSeeding, setIsSeeding] = useState(false);
    const [displayInfoModal, setDisplayInfoModal] = useState(false);
    const [message, setMessage] = useState("");
    const [showViewModal, setShowViewModal] = useState(false);
    const [viewImages, setViewImages] = useState<UploadedImage[]>([]);
    const [viewIndex, setViewIndex] = useState(0);
    const seededFileAttachmentsRef = useRef<Map<File, InitialAttachment>>(new Map());
    const removedAttachmentIdsRef = useRef<Set<string>>(new Set());
    const currentFileCountRef = useRef(0);
    const seededKeyRef = useRef<string | null>(null);
    const seedInFlightRef = useRef(false);
    // Images the code itself is removing from the zone, not the user. onRemoveFile deletes the
    // server row, so these ids mark the ones it should skip.
    const programmaticRemovalsRef = useRef<Set<string>>(new Set());

    const [removeConfirmName, setRemoveConfirmName] = useState<string | null>(null);
    const removeConfirmResolverRef = useRef<((confirmed: boolean) => void) | null>(null);

    const resolveRemoveConfirm = useCallback((confirmed: boolean) => {
        const resolve = removeConfirmResolverRef.current;
        removeConfirmResolverRef.current = null;
        setRemoveConfirmName(null);
        resolve?.(confirmed);
    }, []);

    // The exact File objects the code is removing, rather than a time window. FilePond drains
    // removals across later frames - and setFiles([]) removes them later still - so any timer-based
    // flag was already lowered by the time onRemoveFile ran, letting a save report
    // '"<name>" deleted.' for an image the user never touched.
    const filesBeingClearedRef = useRef<Set<File>>(new Set());

    // Only a removal that went through the confirmation prompt is the user's. Everything else -
    // clearing on save, reseeding, switching equipment, FilePond reconciling its own items after an
    // upload - is code-driven and must stay silent. Tracking what the user confirmed is reliable;
    // trying to enumerate the many ways the code removes files was not.
    const userConfirmedRemovalsRef = useRef<Set<File>>(new Set());

    const consumeUserRemoval = useCallback((file: any) => {
        const source = (file?.file ?? file) as File;
        if (!source || !userConfirmedRemovalsRef.current.has(source)) return false;
        userConfirmedRemovalsRef.current.delete(source);
        return true;
    }, []);

    const beforeRemoveFile = useCallback((file: any) => {
        const sourceFile = (file?.file ?? file) as File;

        if (isRejectedFile(file)) return true;

        // Removed by the code rather than the user - go through without asking.
        if (filesBeingClearedRef.current.has(sourceFile)) return true;
        const attachment = seededFileAttachmentsRef.current.get(sourceFile);
        if (attachment && programmaticRemovalsRef.current.has(attachment.attachmentId)) {
            return true;
        }

        return new Promise<boolean>((resolve) => {
            removeConfirmResolverRef.current = (confirmed: boolean) => {
                // Only a confirmed removal is the user's, and only that one reports back.
                if (confirmed && sourceFile) userConfirmedRemovalsRef.current.add(sourceFile);
                resolve(confirmed);
            };
            setRemoveConfirmName(file?.filename ?? sourceFile?.name ?? "this image");
        });
    }, []);

    // Refresh key the zone has already reseeded for, so a save-triggered reseed happens once.
    const lastRefreshKeyRef = useRef<any>(attachmentsRefreshKey);

    // Matches an entry against the seeded (already-saved) images. Entries arrive either as FilePond
    // items (item.file holds the File) or, right after seeding, as the raw File objects handed to
    // setFiles - both shapes have to resolve to the same File the seeded map is keyed by.
    const isSeededEntry = useCallback((item: any) => {
        if (!item) return false;
        const source = (item.file ?? item) as File;
        return seededFileAttachmentsRef.current.has(source);
    }, []);

    const clearPondSilently = useCallback(() => {
        const held = fileRef?.current?.getFiles?.() ?? [];
        held.forEach((item: any) => {
            const attachment = item?.file && seededFileAttachmentsRef.current.get(item.file as File);
            if (attachment) {
                programmaticRemovalsRef.current.add(attachment.attachmentId);
            }
            // Every held file is being removed by the code, seeded or not. Recorded by identity so
            // the removal can be recognised whenever FilePond gets round to it.
            const source = (item?.file ?? item) as File;
            if (source) filesBeingClearedRef.current.add(source);
        });
        fileRef?.current?.removeFiles();
    }, []);

    
    const fetchAttachmentBlob = useCallback(async (attachment: InitialAttachment): Promise<Blob | null> => {
        if (!uniqueInspectionTaskId || !equipmentId) return null;
        try {
            const pathRes = await zlink.get(`${baseURL}api/v1/inspection/inspectiontask/${uniqueInspectionTaskId}/equipments/${equipmentId}/attachments/${attachment.attachmentId}`);
            const fullPath = pathRes?.status === "done" ? pathRes.data?.fullPath : null;
            if (!fullPath) return null;

            const response = await fetch(`${baseURL}api/v1/common/files/${attachment.attachmentId}/download`, {
                method: "POST",
                cache: "no-store",
                headers: {
                    "Content-Type": "application/json; charset=utf-8",
                    Authorization: sessionStorage.getItem("accesstoken") ?? "",
                },
                body: JSON.stringify({ FullPath: fullPath }),
            });
            if (!response.ok) return null;
            return await response.blob();
        } catch {
            return null;
        }
    }, [uniqueInspectionTaskId, equipmentId]);

    useEffect(() => {
        // Previous/Next updates equipmentId at once but initialAttachments only after the new lists
        // load, so for a few renders the two describe different equipment. Seeding then asks for the
        // previous equipment's files under the new equipment's id and every download 404s.
        if (attachmentsEquipmentId !== undefined && attachmentsEquipmentId !== equipmentId) return;

        const attachments = (initialAttachments ?? []).filter(
            (a) => !removedAttachmentIdsRef.current.has(a.attachmentId)
        );
        const seedKey = `${equipmentId ?? ""}:${attachments.map((a) => a.attachmentId).join(",")}`;
        if (seedInFlightRef.current || seededKeyRef.current === seedKey) return;

        // Normally the user's own picks must not be wiped by a reseed. Straight after a save they
        // must be: they are now saved, so they have to be replaced by the seeded copies or they keep
        // counting as unsaved work.
        const hasUnsavedPicks = (fileRef?.current?.getFiles?.() ?? []).some(
            (item: any) => !isSeededEntry(item)
        );
        if (hasUnsavedPicks && attachmentsRefreshKey === lastRefreshKeyRef.current) return;
        lastRefreshKeyRef.current = attachmentsRefreshKey;

        // Nothing to seed yet. Do not record the key: the attachment lists arrive after equipmentId,
        // so treating this as "done" would stop the real list from ever being seeded.
        if (!attachments.length) {
            if (seededKeyRef.current !== null) {
                seededKeyRef.current = seedKey;
                clearPondSilently();
                currentFileCountRef.current = 0;
                setFiles([]);
            }
            return;
        }

        // Cannot fetch without both ids - they can still be undefined on the first renders.
        if (!uniqueInspectionTaskId || !equipmentId) return;

        seedInFlightRef.current = true;
        // Mirrored into state as well as the ref: the ref alone cannot re-render the form to disable
        // its buttons while the already-saved images are being fetched.
        if (attachments.length) setIsSeeding(true);

        (async () => {
            try {
                // Fetched one at a time on purpose. Every category seeds at once, so downloading
                // in parallel put enough concurrent load on the download endpoint to make it fail.
                const validFiles: File[] = [];
                for (const attachment of attachments) {
                    const blob = await fetchAttachmentBlob(attachment);
                    if (!blob) continue;
                    const file = new File([blob], attachment.fileName, { type: blob.type });
                    seededFileAttachmentsRef.current.set(file, attachment);
                    validFiles.push(file);
                }

                // Only mark this list as seeded once it actually produced files. Recording the key up
                // front meant a failed or empty fetch left the zone blank with no chance of a retry.
                if (!validFiles.length) return;
                seededKeyRef.current = seedKey;

                clearPondSilently();
                currentFileCountRef.current = validFiles.length;
                setFiles(validFiles);
                // The freshly seeded files are brand new File objects the user may well delete, so
                // they must never be treated as code removals.
                validFiles.forEach((file) => filesBeingClearedRef.current.delete(file));
            } finally {
                seedInFlightRef.current = false;
                setIsSeeding(false);
            }
        })();
    }, [initialAttachments, attachmentsEquipmentId, attachmentsRefreshKey, equipmentId, uniqueInspectionTaskId, fetchAttachmentBlob, isSeededEntry, clearPondSilently]);

    const getTempStorageDirectory = useCallback((uploadedFiles: any) => {
        let tempFileIds: string = "";
        uploadedFiles?.forEach((item: any) => {
            if (!item?.serverId) return;
            if (item?.file && seededFileAttachmentsRef.current.has(item.file as File)) return;
            try {
                const response = JSON.parse(JSON.parse(JSON.stringify(item.serverId)));
                if (response?.status === "done") {
                    const directoryId = response.data?.uniqueDirectoryId[0];
                    if (directoryId) {
                        tempFileIds += directoryId + ",";
                    }
                }
            } catch {
            }
        });
        return tempFileIds.slice(0, -1);
    }, []);

    const countUnsavedFiles = useCallback((uploadedFiles: any) =>
        (uploadedFiles ?? []).filter((item: any) =>
            !isSeededEntry(item) && !isRejectedFile(item)
        ).length, [isSeededEntry]);

    const onFilesChangeRef = useRef(onFilesChange);
    const onUploadingChangeRef = useRef(onUploadingChange);
    const onBusyChangeRef = useRef(onBusyChange);

    useEffect(() => {
        onFilesChangeRef.current = onFilesChange;
        onUploadingChangeRef.current = onUploadingChange;
        onBusyChangeRef.current = onBusyChange;
    });

    useEffect(() => {
        currentFileCountRef.current = files?.length ?? 0;
        onFilesChangeRef.current(getTempStorageDirectory(files), files?.length ?? 0, countUnsavedFiles(files));
    }, [files, getTempStorageDirectory, countUnsavedFiles]);

    useEffect(() => {
        onUploadingChangeRef.current(isUploading);
    }, [isUploading]);

    useEffect(() => {
        onBusyChangeRef.current(isUploading || isSeeding);
    }, [isUploading, isSeeding]);

    // A zone that unmounts mid-load (the damper zones are removed when the classification changes)
    // would otherwise leave its "busy" reported forever, disabling the buttons for good.
    useEffect(() => () => { onBusyChangeRef.current(false); }, []);

    useImperativeHandle(ref, () => ({
        getTempStorageDirectory: () => getTempStorageDirectory(files),
        getFileCount: () => files?.length ?? 0,
        isUploading: () => isUploading,
        reset: () => {
            clearPondSilently();
            setFiles([]);
            setIsUploading(false);
            currentFileCountRef.current = 0;
            seededKeyRef.current = null;
            // Deliberately not clearing filesBeingClearedRef: setFiles([]) removes the items on a
            // later frame, and those removals still have to be recognised as code-driven.
            seededFileAttachmentsRef.current = new Map();
            removedAttachmentIdsRef.current = new Set();
        },
        deleteAllImages: async () => {
            // Snapshot the saved images before clearing: reset() wipes seededFileAttachmentsRef, so
            // afterwards there is nothing left to tell us what to delete on the server.
            const savedAttachments = Array.from(seededFileAttachmentsRef.current.values());

            clearPondSilently();
            setFiles([]);
            setIsUploading(false);
            currentFileCountRef.current = 0;
            seededKeyRef.current = null;
            seededFileAttachmentsRef.current = new Map();

            if (!uniqueInspectionTaskId || !equipmentId) return;
            for (const attachment of savedAttachments) {
                // Marked as removed so a later reseed does not pull the deleted image back in.
                removedAttachmentIdsRef.current.add(attachment.attachmentId);
                try {
                    await zlink.post(
                        `${baseURL}api/v1/inspection/inspectiontask/${uniqueInspectionTaskId}/equipments/${equipmentId}/attachments/${attachment.attachmentId}/remove`,
                        attachment.fileName
                    );
                } catch {
                    // Reported by the caller, which owns the confirmation dialog.
                }
            }
        },
    }));

    const showMaxFilesMessage = () => {
        setMessage(`A maximum of ${maxFiles} image${maxFiles > 1 ? "s" : ""} can be uploaded.`);
        setDisplayInfoModal(true);
    };

    const beforeAddFile = (file: any) => {
        const isSeededFile = file?.file
            ? seededFileAttachmentsRef.current.has(file.file as File)
            : seededFileAttachmentsRef.current.has(file as File);
        if (isSeededFile) return true;

        const incomingFileName = (file?.filename ?? file?.name ?? "");
        const incomingType = (file?.file?.type ?? file?.type ?? "").toLowerCase();
        const extension = incomingFileName.includes(".")
            ? incomingFileName.slice(incomingFileName.lastIndexOf(".")).toLowerCase()
            : "";
        const isSupported = ACCEPTED_FILE_EXTENSIONS.includes(extension)
            && (!incomingType || ACCEPTED_FILE_TYPES.includes(incomingType));
        if (!isSupported) {
            setMessage(`"${incomingFileName}" is not a supported image.Choose a JPG, JPEG or PNG file.`);
            setDisplayInfoModal(true);
            return false;
        }
        const incomingName = (file?.filename ?? file?.name ?? "").trim().toLowerCase();
        const isDuplicateName = incomingName.length > 0 && (fileRef?.current?.getFiles?.() ?? []).some((item: any) => {
            if (item?.id && file?.id && item.id === file.id) return false;
            const heldName = (item?.filename ?? item?.file?.name ?? "").trim().toLowerCase();
            return heldName === incomingName;
        });
        if (isDuplicateName) {
            setMessage(`An image named "${file?.filename ?? file?.name}" is already attached.`);
            setDisplayInfoModal(true);
            return false;
        }

        const incomingSize = (file?.file?.size ?? file?.fileSize ?? file?.size ?? 0) as number;
        if (incomingSize > MAX_FILE_SIZE_BYTES) {
            setMessage(`"${file?.filename ?? file?.name}" is ${formatFileSize(incomingSize)}. The maximum allowed size is ${MAX_FILE_SIZE_MB}MB.`);
            setDisplayInfoModal(true);
            return false;
        }

        if (currentFileCountRef.current >= maxFiles) {
            setMessage(`A maximum of ${maxFiles} images can be uploaded.`);
            setDisplayInfoModal(true);
            return false;
        }
        currentFileCountRef.current += 1;
        return true;
    };

    const onRemoveFile = useCallback(async (error: any, file: any) => {
        currentFileCountRef.current = Math.max(0, currentFileCountRef.current - 1);
        if (error) return;

        // Anything the user did not explicitly confirm is a code removal - clearing on save,
        // reseeding, switching equipment, FilePond reconciling its own items - and stays silent.
        const isUserRemoval = consumeUserRemoval(file);

        const sourceFile = file?.file as File | undefined;
        const removedName = file?.filename ?? sourceFile?.name ?? "Image";
        if (!sourceFile) return;
        const attachment = seededFileAttachmentsRef.current.get(sourceFile);

        if (!attachment || !uniqueInspectionTaskId || !equipmentId) {
            if (isUserRemoval) {
                setMessage(`"${removedName}" deleted.`);
                setDisplayInfoModal(true);
            }
            return;
        }

        // Removed by the code, not the user - leave the server row alone and stay silent. Checked
        // against the confirmation too: only an explicit delete may remove the stored image.
        if (!isUserRemoval || programmaticRemovalsRef.current.has(attachment.attachmentId)) {
            programmaticRemovalsRef.current.delete(attachment.attachmentId);
            seededFileAttachmentsRef.current.delete(sourceFile);
            return;
        }

        seededFileAttachmentsRef.current.delete(sourceFile);
        removedAttachmentIdsRef.current.add(attachment.attachmentId);
        try {
            const res = await zlink.post(
                `${baseURL}api/v1/inspection/inspectiontask/${uniqueInspectionTaskId}/equipments/${equipmentId}/attachments/${attachment.attachmentId}/remove`,
                attachment.fileName
            );
            // Report the real outcome: the image is already gone from the zone, so a failed delete
            // must say so rather than claim success the server did not give.
            if (res?.status === "done") {
                setMessage(`"${removedName}" deleted.`);
            } else {
                setMessage(`"${removedName}" could not be deleted. Please try again.`);
            }
        } catch {
            setMessage(`"${removedName}" could not be deleted. Please try again.`);
        }
        setDisplayInfoModal(true);
    }, [uniqueInspectionTaskId, equipmentId]);

    const onWarning = (error: any, file: any) => {
        if (error && error.body === "Max files") {
            showMaxFilesMessage();
        }
    };

    const serverConfig: any = {
        process: (fieldName: any, file: any, metadata: any, load: any, error: any, progress: any, abort: any) => {
            if (seededFileAttachmentsRef.current.has(file as File)) {
                load("");
                return { abort: () => abort() };
            }

            const formData = new FormData();
            formData.append("files", file, file.name);

            const request = new XMLHttpRequest();
            request.open("POST", `${baseURL}api/v1/common/files/uploadfailedequipments`);
            request.setRequestHeader("Authorization", sessionStorage.getItem("accesstoken") ?? "");

            request.upload.onprogress = (e: any) => {
                progress(e.lengthComputable, e.loaded, e.total);
            };

            request.onload = () => {
                if (request.status >= 200 && request.status < 300) {
                    load(request.responseText);
                } else {
                    error(`Upload failed with status ${request.status}`);
                }
            };
            request.onerror = () => error("Upload failed");

            request.send(formData);

            return {
                abort: () => {
                    request.abort();
                    abort();
                },
            };
        },
    };

    const onProcessFileStart = () => {
        setIsUploading(true);
    };

    const onProcessFile = (error: any, file: any) => {
        setIsUploading(false);
        const liveFiles = fileRef?.current?.getFiles?.() ?? [];
        setFiles(liveFiles);
        onFilesChangeRef.current(getTempStorageDirectory(liveFiles), liveFiles?.length ?? 0, countUnsavedFiles(liveFiles));
    };

    const onProcessFiles = () => {
        setIsUploading(false);
        const liveFiles = fileRef?.current?.getFiles?.() ?? [];
        setFiles(liveFiles);
        onFilesChangeRef.current(getTempStorageDirectory(liveFiles), liveFiles?.length ?? 0, countUnsavedFiles(liveFiles));
    };

    const onFileError = () => {
        setIsUploading(false);
    };


    const getImages = useCallback((): UploadedImage[] => {
        return (files ?? [])
            .filter((item: any) => item?.file && !isRejectedFile(item))
            .map((item: any) => {
                let tempStorageDirectory = "";
                try {
                    const response = JSON.parse(JSON.parse(JSON.stringify(item.serverId)));
                    if (response?.status === "done") {
                        tempStorageDirectory = response.data?.uniqueDirectoryId[0] ?? "";
                    }
                } catch {
                    // Still processing - no serverId to parse yet.
                }
                return {
                    id: item.id,
                    fileName: item.filename ?? item.file?.name,
                    tempStorageDirectory,
                    previewUrl: "",
                    file: item.file,
                };
            });
    }, [files]);

    const hasImages = (files ?? []).some((item: any) => item?.file && !isRejectedFile(item));

    const onViewClick = () => {
        const images = getImages();
        if (!images.length) {
            setMessage("Upload an image to view.");
            setDisplayInfoModal(true);
            return;
        }
        setViewImages(images.map((image) => ({ ...image, previewUrl: URL.createObjectURL(image.file) })));
        setViewIndex(0);
        setShowViewModal(true);
    };

    const onDownloadClick = () => {
        const images = getImages();
        if (!images.length) {
            setMessage("Upload an image to download.");
            setDisplayInfoModal(true);
            return;
        }
        // Files are still local, so downloaded from the browser without a server call.
        images.forEach((image) => {
            const link = document.createElement("a");
            link.href = URL.createObjectURL(image.file);
            link.download = image.fileName;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            URL.revokeObjectURL(link.href);
        });
    };

    return (
        <>
            <div className="field image-upload-field">
                <span className="p-float-label">
                    <div className="flex align-items-center flex-column upload-file-section">
                        <div className="upload-detail">
                            <FilePond
                                ref={fileRef}
                                id={`${fieldKey}-image-upload`}
                                allowMultiple={maxFiles > 1}
                                maxFiles={maxFiles + 1}
                                files={files}
                                onupdatefiles={setFiles}
                                server={serverConfig}
                                beforeAddFile={beforeAddFile}
                                onwarning={onWarning}
                                onprocessfilestart={onProcessFileStart}
                                onprocessfile={onProcessFile}
                                onprocessfiles={onProcessFiles}
                                onerror={onFileError}
                                beforeRemoveFile={beforeRemoveFile}
                                onremovefile={onRemoveFile}
                                disabled={disabled}
                                name="files"
                                credits={false}
                                labelIdle={labelIdle}
                                labelFileLoading=""
                                labelTapToUndo=""
                                labelFileProcessing=""
                                labelFileProcessingComplete=""
                                labelFileProcessingAborted=""
                                labelFileProcessingError=""
                                labelButtonAbortItemProcessing=""
                                labelButtonUndoItemProcessing=""
                                labelButtonRetryItemProcessing=""
                                labelButtonProcessItem=""
                                labelTapToCancel=""
                                labelFileRemoveError=""
                                labelTapToRetry=""
                                allowFileTypeValidation={false}
                                acceptedFileTypes={ACCEPTED_FILE_TYPES}
                            />
                            <span className="upload-hint">(JPG, JPEG, PNG up to {MAX_FILE_SIZE_MB}MB)</span>

                        </div>
                    </div>
                </span>

                {hasImages && (
                    <div className="image-upload-actions">
                        <span className="action-link" onClick={onViewClick} title="View image">
                            <BsEye />
                            <span>View</span>
                        </span>
                        <span className="action-link" onClick={onDownloadClick} title="Download image">
                            <BsDownload />
                            <span>Download</span>
                        </span>
                    </div>
                )}
            </div>

            {showViewModal && viewImages.length > 0 && (
                <Dialog
                    header={`View Image (${viewImages[viewIndex]?.fileName})`}
                    visible={showViewModal}
                    style={{ maxWidth: "50%" }}
                    onHide={() => {
                        viewImages.forEach((image) => URL.revokeObjectURL(image.previewUrl));
                        setShowViewModal(false);
                        setViewImages([]);
                    }}
                    className="view-file-modal"
                >
                    <div className="content-box">
                        <div className="file-view-wrapper">
                            <img src={viewImages[viewIndex]?.previewUrl} className="image-file" alt={viewImages[viewIndex]?.fileName} />
                        </div>
                        {viewImages.length > 1 && (
                            <div className="image-view-pager">
                                <Button
                                    label="Previous"
                                    className="cancel-btn"
                                    disabled={viewIndex === 0}
                                    onClick={() => setViewIndex(viewIndex - 1)}
                                />
                                <span className="pager-count">{viewIndex + 1} of {viewImages.length}</span>
                                <Button
                                    label="Next"
                                    className="cancel-btn"
                                    disabled={viewIndex >= viewImages.length - 1}
                                    onClick={() => setViewIndex(viewIndex + 1)}
                                />
                            </div>
                        )}
                    </div>
                </Dialog>
            )}

            <InformationBox
                showInfoModal={displayInfoModal}
                setShowInfoModal={setDisplayInfoModal}
                message={message}
            />

            <ConfirmationBox
                showConfirmModal={removeConfirmName !== null}
                setShowConfirmModal={() => resolveRemoveConfirm(false)}
                confirmAction={() => resolveRemoveConfirm(true)}
                cancelAction={() => resolveRemoveConfirm(false)}
                onHideAction={() => resolveRemoveConfirm(false)}
                message={`Are you sure you want to delete "${removeConfirmName}"?`}
            />
        </>
    );
});

export default memo(ImageUploadZone);
